import { ReportActionStatus, ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import { AccessGuard } from '@/components/Analysis/shared/AccessGuard';
import * as state from '@/components/Analysis/state';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { Snackbar } from '@/components/shared/Snackbar';
import { StructureCheck } from '@/components/shared/StructureCheck';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors } from '@/theme/colors';
import { UpgradePromptBackground } from '@/types';
import { PictureAsPdf } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Tab } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { UpgradePrompt } from '../../shared/UpgradePrompt';
import { useFetchReportGroups } from '../hooks/useFetchReportGroups';
import { ReportGroups } from './ReportGroups';
import { ReportTypeTabs } from './ReportTypeTabs';
import { ReportsList } from './ReportsList';
import { FloatingLine, Panel, ReportTabs, ReportsMainStyles, containerStyles } from './styles';
import { useBulkReportDownload } from './useBulkReportDownload';
import { useReportFilters } from './useReportFilters';
import { SelectedValues } from '../../../shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { LocationTag } from '@/constants';
import { formatEnumForDisplay } from '@/utils';

const reportsTabs = [
  { key: 'ALL', label: 'All' },
  { key: ReportStatus.Draft, label: 'Drafts' },
  { key: ReportStatus.Published, label: 'Open' },
  { key: ReportStatus.Closed, label: 'Closed' },
];

const REPORT_FILTERS = [
  {
    id: 'actionStatus',
    name: 'Status',
    options: Object.values(ReportActionStatus).map((actionStatus) =>
      formatEnumForDisplay(actionStatus)
    ),
    key: 'actionStatus',
  },
];

export const ReportsLanding = () => {
  const [selectedReportFilters, setSelectedReportFilters] = useAtom(state.selectedReportFilters);
  const [createdByMe, setCreatedByMe] = useAtom(state.createdByMeFilter);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState<ReportStatus | 'ALL'>('ALL');
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedReportType, setSelectedReportType] = useState<ReportInternalType>(
    ReportInternalType.PointOfInterest
  );

  const structureId = useGetStructureId();
  const navbarHeight = useNavbarHeight();
  const { filters: componentFilters } = useReportFilters();
  const { reportGroups } = useFetchReportGroups(selectedReportType, currentTab, searchTerm);
  const { handleBulkDownload } = useBulkReportDownload({
    selectedReportIds,
    setLoading,
  });

  const { shouldShow, content, persistent, duration } = snackbarMessage;

  const selectedReportTypeIsComponent = useMemo(() => {
    return selectedReportType === ReportInternalType.Component;
  }, [selectedReportType]);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const handleReportTypeChange = useCallback(
    (_: React.SyntheticEvent, newValue: ReportInternalType) => {
      setSelectedReportType(newValue);
    },
    [setSelectedReportType]
  );

  const handleTabActiveChange = (event: React.SyntheticEvent, newValue: ReportStatus) => {
    setCurrentTab(newValue);
  };

  const handleSelectedFilterValuesChange = useCallback(
    (selectedValues: SelectedValues) => {
      const filteredSelectedValues = Object.fromEntries(
        Object.entries(selectedValues).filter(([, values]) => values.length > 0)
      );
      setSelectedReportFilters(filteredSelectedValues);
    },
    [setSelectedReportFilters]
  );

  const filters = useMemo(
    () =>
      selectedReportTypeIsComponent ? [...componentFilters, ...REPORT_FILTERS] : REPORT_FILTERS,
    [selectedReportTypeIsComponent, componentFilters]
  );

  const toggleFilters = useMemo(
    () => [
      {
        label: 'Created by Me',
        checked: createdByMe,
        onChange: setCreatedByMe,
      },
    ],
    [createdByMe, setCreatedByMe]
  );
  return (
    <StructureCheck structureId={structureId}>
      <AccessGuard structureId={structureId} feature="reports">
        <UpgradePrompt
          feature="reports"
          featureTitle="Reports"
          background={UpgradePromptBackground.Reports}
          paragraphs={[
            'Access enhanced reports with automated site images',
            'View marked location of anomalies',
            'Export reports for streamlined tracking, seamless monitoring, and documentation purposes',
            'Add selected images of anomalies for greater clarity',
          ]}
        >
          <Stack
            direction="row"
            justifyContent="center"
            sx={containerStyles}
            height={`calc(100vh - ${navbarHeight}px)`}
          >
            <Box sx={ReportsMainStyles}>
              <ReportTabs value={currentTab} onChange={handleTabActiveChange}>
                {reportsTabs.map((tab) => (
                  <Tab
                    value={tab.key}
                    key={tab.key}
                    label={tab.label}
                    sx={{ textTransform: 'none' }}
                  />
                ))}
              </ReportTabs>
              <FloatingLine />
              <ReportTypeTabs
                selectedTab={selectedReportType}
                handleChange={handleReportTypeChange}
              />
              <Panel>
                <FilterAndSearchPanel
                  key={`filter-panel-${selectedReportType}`}
                  templateFields={filters}
                  selectedValues={selectedReportFilters}
                  setSelectedValues={setSelectedReportFilters}
                  handleSelectedChange={handleSelectedFilterValuesChange}
                  searchBar
                  searchPlaceholder="Search report"
                  searchTerm={searchTerm}
                  setSearch={setSearchTerm}
                  filterOptions
                  hideStaticFilters
                  location={LocationTag.Reports}
                  toggleFilters={toggleFilters}
                  endAdornment={
                    selectedReportTypeIsComponent ? (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          minWidth: 'auto',
                          whiteSpace: 'nowrap',
                          height: '3.2rem',
                        }}
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} sx={{ color: abyssColors.primary[500] }} />
                          ) : (
                            <PictureAsPdf sx={{ cursor: 'pointer' }} />
                          )
                        }
                        onClick={handleBulkDownload}
                        disabled={loading || selectedReportIds.length === 0}
                      >
                        Download PDF
                      </Button>
                    ) : null
                  }
                />
              </Panel>
              {reportGroups.length === 0 ? (
                <ReportsList
                  selectedReportType={selectedReportType}
                  status={currentTab}
                  searchTerm={searchTerm}
                  selectedReportIds={selectedReportIds}
                  setSelectedReportIds={setSelectedReportIds}
                />
              ) : (
                <>
                  {reportGroups.map((group) => (
                    <ReportGroups
                      key={group.groupId}
                      reportGroup={group}
                      selectedReportType={selectedReportType}
                      status={currentTab}
                      searchTerm={searchTerm}
                      selectedReportIds={selectedReportIds}
                      setSelectedReportIds={setSelectedReportIds}
                    />
                  ))}
                </>
              )}
              <Snackbar
                open={shouldShow}
                onClose={closeSnack}
                persistent={persistent}
                autoHideDuration={duration}
              >
                {content}
              </Snackbar>
            </Box>
          </Stack>
        </UpgradePrompt>
      </AccessGuard>
    </StructureCheck>
  );
};
